import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator, CommandShortcut,
} from "./ui/command";
import {
  BarChart3, Bell, Brain, Building2, Home, Moon, Plus,
  Search, Settings, Sparkles, Sun, Target, User, Loader2, FolderOpen,
} from "lucide-react";
import { useTheme } from "../contexts/ThemeContext";
import { getApiUrl } from "../utils/config";

const RECENT_KEY = "syncwork_recent_commands";
const readRecent = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || "[]"); } catch { return []; } };
const writeRecent = (items) => localStorage.setItem(RECENT_KEY, JSON.stringify(items.slice(0, 6)));

const STATUS_LABEL = { todo: "To Do", in_progress: "In Progress", in_review: "In Review", approved: "Approved", done: "Done" };

const GlobalCommandPalette = ({ currentOrg, onQuickCreateTask, onQuickCreateProject, onNavigateNotifications, forceOpen, onForceOpenConsumed }) => {
  const navigate = useNavigate();
  const { theme, setTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Allow external trigger (e.g. header search button)
  useEffect(() => {
    if (forceOpen) { setOpen(true); onForceOpenConsumed?.(); }
  }, [forceOpen, onForceOpenConsumed]);
  const [recentActions, setRecentActions] = useState(readRecent());
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState({ tasks: [], projects: [], members: [] });
  const debounceRef = useRef(null);
  const controllerRef = useRef(null);

  // Cmd/Ctrl + K
  useEffect(() => {
    const down = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen(prev => !prev);
      }
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  // Reset on close
  useEffect(() => {
    if (!open) { setQuery(""); setResults({ tasks: [], projects: [], members: [] }); }
  }, [open]);

  // Debounced search
  const doSearch = useCallback(async (q) => {
    if (!currentOrg?.id || q.trim().length < 2) {
      setResults({ tasks: [], projects: [], members: [] });
      setSearching(false);
      return;
    }
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    setSearching(true);
    try {
      const res = await axios.get(
        getApiUrl(`/organizations/${currentOrg.id}/search`),
        { params: { q: q.trim() }, signal: controllerRef.current.signal }
      );
      setResults(res.data || { tasks: [], projects: [], members: [] });
    } catch (err) {
      if (err.name !== "CanceledError" && err.code !== "ERR_CANCELED") {
        setResults({ tasks: [], projects: [], members: [] });
      }
    } finally {
      setSearching(false);
    }
  }, [currentOrg?.id]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  const baseActions = useMemo(() => [
    { id: "go-dashboard",   label: "Dashboard",        icon: Home,       shortcut: "G D", run: () => navigate("/dashboard") },
    { id: "go-projects",    label: "Projects",         icon: FolderOpen, shortcut: "G P", run: () => navigate("/projects") },
    { id: "go-analytics",   label: "Analytics",        icon: BarChart3,  shortcut: "G A", run: () => navigate("/analytics") },
    { id: "go-settings",    label: "Settings",         icon: Settings,   shortcut: "G S", run: () => navigate("/settings") },
    { id: "create-task",    label: "Create Task",      icon: Plus,       shortcut: "C T", run: () => onQuickCreateTask?.("modal") },
    { id: "create-project", label: "Create Project",   icon: Building2,  shortcut: "C P", run: () => onQuickCreateProject?.("modal") },
    { id: "notifications",  label: "Notifications",    icon: Bell,       shortcut: "N",   run: () => onNavigateNotifications?.() },
    { id: "theme-light",    label: "Light Theme",      icon: Sun,        shortcut: "",    run: () => setTheme("light") },
    { id: "theme-dark",     label: "Dark Theme",       icon: Moon,       shortcut: "",    run: () => setTheme("dark") },
    { id: "theme-system",   label: "System Theme",     icon: Target,     shortcut: "",    run: () => setTheme("system") },
    { id: "user-guide",     label: "Open User Guide",  icon: Sparkles,   shortcut: "",    run: () => window.open("/guide", "_blank") },
  ], [navigate, onNavigateNotifications, onQuickCreateProject, onQuickCreateTask, setTheme]);

  const execute = (action) => {
    action.run();
    const next = [action.id, ...recentActions.filter(i => i !== action.id)];
    setRecentActions(next);
    writeRecent(next);
    setQuery("");
    setOpen(false);
  };

  const hasQuery = query.trim().length >= 2;
  const hasResults = results.tasks.length > 0 || results.projects.length > 0 || results.members.length > 0;

  const recentModels = recentActions
    .map(id => baseActions.find(a => a.id === id))
    .filter(Boolean);

  const filteredBase = hasQuery
    ? baseActions.filter(a => a.label.toLowerCase().includes(query.toLowerCase()))
    : baseActions;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput
        value={query}
        onValueChange={setQuery}
        placeholder="Search tasks, projects, members, actions…"
      />
      <CommandList>
        <CommandEmpty>
          {searching ? (
            <span className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </span>
          ) : hasQuery ? "No results found." : "Type to search or pick an action."}
        </CommandEmpty>

        {/* Search results */}
        {hasQuery && hasResults && (
          <>
            {results.tasks.length > 0 && (
              <CommandGroup heading={`Tasks (${results.tasks.length})`}>
                {results.tasks.map(task => (
                  <CommandItem
                    key={task.id}
                    onSelect={() => {
                      navigate(`/projects/${task.project_id}/board`, { state: { openTaskId: task.id } });
                      setOpen(false); setQuery("");
                    }}
                  >
                    <Target className="mr-2 h-4 w-4 text-indigo-500" />
                    <span className="flex-1 truncate">{task.title}</span>
                    <span className="ml-2 text-xs text-muted-foreground truncate max-w-[120px]">{task.project_name}</span>
                    {task.status && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
                        {STATUS_LABEL[task.status] || task.status}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.projects.length > 0 && (
              <CommandGroup heading={`Projects (${results.projects.length})`}>
                {results.projects.map(proj => (
                  <CommandItem
                    key={proj.id}
                    onSelect={() => {
                      navigate(`/projects/${proj.id}/board`);
                      setOpen(false); setQuery("");
                    }}
                  >
                    <FolderOpen className="mr-2 h-4 w-4 text-violet-500" />
                    <span className="flex-1 truncate">{proj.name}</span>
                    {proj.description && (
                      <span className="ml-2 text-xs text-muted-foreground truncate max-w-[140px]">{proj.description}</span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            {results.members.length > 0 && (
              <CommandGroup heading={`Members (${results.members.length})`}>
                {results.members.map(m => (
                  <CommandItem
                    key={m.id}
                    onSelect={() => { navigate("/team"); setOpen(false); setQuery(""); }}
                  >
                    <User className="mr-2 h-4 w-4 text-emerald-500" />
                    <span className="flex-1 truncate">{m.full_name || m.email}</span>
                    {m.full_name && <span className="ml-2 text-xs text-muted-foreground">{m.email}</span>}
                  </CommandItem>
                ))}
              </CommandGroup>
            )}
            <CommandSeparator />
          </>
        )}

        {/* Recent actions (when no query) */}
        {!hasQuery && recentModels.length > 0 && (
          <CommandGroup heading="Recent">
            {recentModels.map(action => (
              <CommandItem key={action.id} onSelect={() => execute(action)}>
                <action.icon className="mr-2 h-4 w-4" />
                <span>{action.label}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Base actions */}
        <CommandGroup heading="Actions">
          {filteredBase.map(action => (
            <CommandItem key={action.id} onSelect={() => execute(action)}>
              <action.icon className="mr-2 h-4 w-4" />
              <span>{action.label}</span>
              {action.shortcut && <CommandShortcut>{action.shortcut}</CommandShortcut>}
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
      <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground flex items-center justify-between">
        <span>Press <kbd className="px-1 py-0.5 rounded bg-muted font-mono text-[10px]">↑↓</kbd> to navigate · <kbd className="px-1 py-0.5 rounded bg-muted font-mono text-[10px]">Enter</kbd> to select</span>
        <span>⌘K to toggle</span>
      </div>
    </CommandDialog>
  );
};

export default GlobalCommandPalette;
